import { useEffect, useState } from 'react';
import { atsApi } from '../services/atsApi';

const photoUrlCache = new Map<string, string>();
const PHOTO_UPDATED_EVENT = 'ats:candidate-photo-updated';

export function invalidateCandidatePhoto(candidateId: string): void {
  const oldUrl = photoUrlCache.get(candidateId);
  if (oldUrl) URL.revokeObjectURL(oldUrl);
  photoUrlCache.delete(candidateId);
  window.dispatchEvent(new CustomEvent(PHOTO_UPDATED_EVENT, { detail: candidateId }));
}

export function candidateHasPhoto(item: { has_photo?: boolean; documents?: unknown[] }): boolean {
  if (item.has_photo) return true;
  return (item.documents || []).some((document) => {
    if (typeof document !== 'object' || document === null || Array.isArray(document)) return false;
    const type = String((document as Record<string, unknown>).document_type || (document as Record<string, unknown>).type || '');
    return type === 'photo';
  });
}

export function CandidatePhoto({
  candidateId,
  hasPhoto,
  initials,
  className,
}: {
  candidateId: string;
  hasPhoto?: boolean;
  initials: string;
  className: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const onUpdated = (event: Event) => {
      if ((event as CustomEvent<string>).detail === candidateId) {
        setUrl(null);
        setRevision((current) => current + 1);
      }
    };
    window.addEventListener(PHOTO_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(PHOTO_UPDATED_EVENT, onUpdated);
  }, [candidateId]);

  useEffect(() => {
    if (!hasPhoto) {
      setUrl(null);
      return;
    }
    const cached = photoUrlCache.get(candidateId);
    if (cached) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    void atsApi.getCandidatePhotoUrl(candidateId).then((nextUrl) => {
      if (cancelled || !nextUrl) return;
      photoUrlCache.set(candidateId, nextUrl);
      setUrl(nextUrl);
    });
    return () => {
      cancelled = true;
    };
  }, [candidateId, hasPhoto, revision]);

  if (url) {
    return <img src={url} alt="" className={`${className} object-cover`} />;
  }

  return <div className={className}>{initials}</div>;
}
